import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import PropTypes from "prop-types";

import Video from "./video";
import MediaLightbox from "../lightbox/mediaLightbox";

import "./videoBackgroundPlayer.scss";

const VideoBackgroundPlayer = ({
  children = null,
  vttUrl = undefined,
  src,
  placeholderImage = undefined,
  muted,
  setMuted,
  playing,
  setPlaying,
  transcriptRef = undefined,
}, ref) => {
  const lightbox = useRef(null);
  const backgroundPlayer = useRef(null);

  useImperativeHandle(ref, () => ({
    openVideo() {
      setMuted(true);
      setPlaying(false);
      backgroundPlayer.current.pause();
      lightbox.current.show();
    },
    toggleMute() {
      setMuted((prevState) => !prevState);
    },
  }), []);

  useEffect(() => {
    if (backgroundPlayer?.current) {
      backgroundPlayer.current.muted(muted);
    }
  }, [muted]);

  useEffect(() => {
    if (backgroundPlayer?.current) {
      backgroundPlayer.current.loop(playing);
      backgroundPlayer.current.autoplay(playing);

      if (playing) {
        backgroundPlayer.current.play();
      } else {
        backgroundPlayer.current.pause();
      }
    }
  }, [playing]);

  const sources = {
    md: src?.md && { src: src.md, type: src.asset.type },
    xl: src?.xl && { src: src.xl, type: src.asset.type },
    xs: src?.xs && { src: src.xs, type: src.asset.type },
  };

  const placeholder = (placeholderImage && placeholderImage.length > 0 && placeholderImage[0].url)
    || src?.asset.thumbnailUrl || "/images/placeholder/placeholder.webp";

  return (
    <>
      <Video
        options={{
          autoplay: true,
          controls: false,
          loop: true,
          muted,
          playsinline: true,
          poster: placeholder,
          responsive: true,
        }}
        vttUrl={vttUrl}
        sources={sources}
        asBackground
        onReady={(player) => {
          backgroundPlayer.current = player;
        }}
      />
      <div className="video-background-player">
        {children}
      </div>
      <MediaLightbox
        video
        onHide={() => {
          setPlaying(true);
          transcriptRef.current?.close();
        }}
        ref={lightbox}
        withConsent={false}
        media={(
          <Video
            transcriptRef={transcriptRef}
            options={{
              autoplay: true,
              controlBar: {
                children: [
                  "playToggle",
                  "volumePanel",
                  "currentTimeDisplay",
                  "timeDivider",
                  "durationDisplay",
                  "progressControl",
                  "subtitlesButton",
                  "fullscreenToggle",
                ],
              },
              controls: true,
              fluid: true,
              loop: false,
              muted: false,
              poster: placeholderImage,
              responsive: true,
            }}
            vttUrl={vttUrl}
            sources={sources}
          />
        )}
      />
    </>
  );
};

VideoBackgroundPlayer.propTypes = {
  children: PropTypes.oneOf([PropTypes.node]),
  muted: PropTypes.bool.isRequired,
  placeholderImage: PropTypes.string,
  playing: PropTypes.bool.isRequired,
  setMuted: PropTypes.func.isRequired,
  setPlaying: PropTypes.func.isRequired,
  src: PropTypes.shape({
    asset: PropTypes.shape({
      thumbnailUrl: PropTypes.string,
      type: PropTypes.string.isRequired,
    }).isRequired,
    md: PropTypes.string,
    xl: PropTypes.string,
    xs: PropTypes.string,
  }).isRequired,
  transcriptRef: PropTypes.node,
  vttUrl: PropTypes.string,
};

// eslint-disable-next-line react/display-name
export default forwardRef((props, ref) => VideoBackgroundPlayer(props, ref));
